// Expert assignment panel for issue detail right column
import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { UserPlus, Zap, Users, Loader2 } from 'lucide-react'
import { useExpertProfiles } from '@/hooks/useExpertProfiles'
import { useExpertAssignment } from '@/hooks/useExpertAssignment'
import { DelegationDialog } from './DelegationDialog'

interface ExpertAssignmentPanelProps {
  issueId: string
  assignments: Array<{
    id: string
    status: string
    assigned_at: string
    expert_profiles?: { full_name: string; email: string } | null
  }>
}

export function ExpertAssignmentPanel({ issueId, assignments }: ExpertAssignmentPanelProps): JSX.Element {
  const { profiles } = useExpertProfiles()
  const { autoAssign, isAutoAssigning, manualAssign } = useExpertAssignment()
  const [selectedExpert, setSelectedExpert] = useState('')
  const [isAssigning, setIsAssigning] = useState(false)
  const [showDelegation, setShowDelegation] = useState(false)

  const handleAutoAssign = async () => {
    try {
      await autoAssign(issueId)
    } catch (err) {
      console.error('Auto-assign failed:', err)
    }
  }

  const handleManualAssign = async () => {
    if (!selectedExpert) return
    setIsAssigning(true)
    try {
      await manualAssign({ issueId, expertId: selectedExpert })
      setSelectedExpert('')
    } catch (err) {
      console.error('Manual assign failed:', err)
    } finally {
      setIsAssigning(false)
    }
  }

  const activeAssignment = assignments.find(
    (a) => a.status !== 'rejected' && a.status !== 'completed'
  )

  return (
    <>
      <Card className="border-kaycoo-teal-light bg-kaycoo-white">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-sm text-kaycoo-teal-dark">
            <Users className="h-4 w-4" />
            Expert Assignment
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {activeAssignment && (
            <div className="rounded-md border border-kaycoo-teal-light bg-kaycoo-teal-pale p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-kaycoo-teal-dark">
                    {(activeAssignment.expert_profiles as { full_name: string } | null)?.full_name ?? 'Expert'}
                  </p>
                  <p className="text-xs text-kaycoo-charcoal-2">
                    Assigned {new Date(activeAssignment.assigned_at).toLocaleDateString()}
                  </p>
                </div>
                <Badge variant="outline" className="capitalize">
                  {activeAssignment.status}
                </Badge>
              </div>
            </div>
          )}

          {!activeAssignment && (
            <>
              <Button
                onClick={handleAutoAssign}
                disabled={isAutoAssigning}
                className="w-full bg-kaycoo-teal-dark text-white hover:bg-kaycoo-teal-dark-2"
                size="sm"
              >
                {isAutoAssigning ? (
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                ) : (
                  <Zap className="mr-1.5 h-4 w-4" />
                )}
                Auto-Assign (AI)
              </Button>

              <div className="flex gap-2">
                <Select value={selectedExpert} onValueChange={setSelectedExpert}>
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Select expert" />
                  </SelectTrigger>
                  <SelectContent>
                    {profiles.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.full_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  onClick={handleManualAssign}
                  disabled={!selectedExpert || isAssigning}
                  variant="outline"
                  size="sm"
                >
                  {isAssigning ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <UserPlus className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </>
          )}

          <Button
            variant="outline"
            size="sm"
            className="w-full border-kaycoo-teal-dark text-kaycoo-teal-dark hover:bg-kaycoo-teal-pale"
            onClick={() => setShowDelegation(true)}
          >
            Delegate
          </Button>

          {assignments.length > 0 && (
            <div className="space-y-1 border-t border-kaycoo-white-3 pt-2">
              <p className="text-xs font-medium text-kaycoo-charcoal-2">History</p>
              {assignments.map((a) => (
                <div key={a.id} className="flex items-center justify-between text-xs text-kaycoo-charcoal-2">
                  <span>{(a.expert_profiles as { full_name: string } | null)?.full_name ?? 'Unknown'}</span>
                  <Badge variant="outline" className="text-xs capitalize">{a.status}</Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {showDelegation && (
        <DelegationDialog
          issueId={issueId}
          onClose={() => setShowDelegation(false)}
        />
      )}
    </>
  )
}
